import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Tabs, message, Switch, Tag, Space } from "antd";
import { getCourseDecisionBox, saveCourseDecisionBox } from "../../../appRedux/actions/courses";
import TextEditor from "../Texteditor";
import AIGenerateAction from "../../Common/AIGenerateAction";

const { TabPane } = Tabs;

const CourseDecisionBox = ({ courseId }) => {
    const dispatch = useDispatch();
    const { decisionBox, courseInfo } = useSelector((state) => state.courses);
    const [loading, setLoading] = useState(false);

    // Use Hex ID if available, otherwise courseId from params
    const effectiveId = courseInfo?._id || courseId;

    // Local state for fields + active flag
    const [formData, setFormData] = useState({
        recommended_for: "",
        not_recommended_for: "",
        price_guidance: "",
        final_verdict: "",
        is_decision_box_active: true, // Default to true if undefined
    });

    // Track which tab is active to display the correct data in the single editor
    const [activeTab, setActiveTab] = useState("recommended_for");

    useEffect(() => {
        if (effectiveId) {
            dispatch(getCourseDecisionBox(effectiveId));
        }
    }, [dispatch, effectiveId]);

    useEffect(() => {
        // Only hydrate if we actually have data in the store
        if (decisionBox && Object.keys(decisionBox).length > 0) {
            setFormData({
                recommended_for: decisionBox.recommended_for || "",
                not_recommended_for: decisionBox.not_recommended_for || "",
                price_guidance: decisionBox.price_guidance || "",
                final_verdict: decisionBox.final_verdict || "",
                is_decision_box_active: ('is_decision_box_active' in decisionBox)
                    ? Boolean(decisionBox.is_decision_box_active)
                    : true, // Default to true only for legacy data that never had this field
            });
        }
    }, [decisionBox]);

    const handleEditorChange = (content) => {
        setFormData((prev) => ({
            ...prev,
            [activeTab]: content,
        }));
    };

    const handleToggleActive = async (checked) => {
        const updatedData = {
            ...formData,
            is_decision_box_active: checked,
        };
        setFormData(updatedData);

        // Auto-save the toggle state immediately
        if (effectiveId) {
            try {
                await dispatch(saveCourseDecisionBox(effectiveId, updatedData));
            } catch (error) {
                // Revert toggle on failure
                setFormData((prev) => ({
                    ...prev,
                    is_decision_box_active: !checked,
                }));
            }
        }
    };

    const handleSave = async () => {
        if (!effectiveId) return;
        setLoading(true);
        try {
            await dispatch(saveCourseDecisionBox(effectiveId, formData));
            // message.success("Decision Box saved successfully!"); // Already handled in action
        } catch (error) {
            // Error handling is also largely in action, but safe fallback:
            console.error("Save error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAIGenerateSuccess = (generatedData) => {
        if (!generatedData || typeof generatedData !== 'object') {
            message.error("Invalid AI response format. Expected a JSON object.");
            return;
        }

        console.log("🔹 AI Generated Data for Decision Box:", generatedData);

        const ensureHtml = (val) => {
            if (!val) return null;
            if (Array.isArray(val)) {
                return `<ul>${val.map(item => `<li>${item}</li>`).join("")}</ul>`;
            }
            return String(val);
        };

        const updatedData = {
            ...formData,
            recommended_for: ensureHtml(generatedData.recommended_for || generatedData.recommendedFor || generatedData.RecommendedFor || generatedData["Recommended For"]) || formData.recommended_for,
            not_recommended_for: ensureHtml(generatedData.not_recommended_for || generatedData.notRecommendedFor || generatedData.NotRecommendedFor || generatedData["Not Recommended For"]) || formData.not_recommended_for,
            price_guidance: ensureHtml(generatedData.price_guidance || generatedData.priceGuidance || generatedData.PriceGuidance || generatedData["Price Guidance"]) || formData.price_guidance,
            final_verdict: ensureHtml(generatedData.final_verdict || generatedData.finalVerdict || generatedData.FinalVerdict || generatedData["Final Verdict"] || generatedData.html || generatedData.content) || formData.final_verdict,
        };
        
        setFormData(updatedData);
        message.info("Decision box content has been generated. Review and click 'Save Changes'.");
    };

    const onTabChange = (key) => {
        setActiveTab(key);
    };

    const isActive = formData.is_decision_box_active;

    return (
        <div className="card">
            <div className="card-body">
                {/* Header Section */}
                <div className="mb-4">
                    <div className="d-flex align-items-center mb-3" style={{ gap: "16px" }}>
                        <h4 className="card-title mb-0" style={{ whiteSpace: "nowrap", fontWeight: 600, fontSize: "16px", margin: 0, color: "#111827" }}>
                            Course Decision Box
                        </h4>
                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            <Switch
                                checked={isActive}
                                onChange={handleToggleActive}
                                checkedChildren="ON"
                                unCheckedChildren="OFF"
                                size="small"
                            />
                            {isActive && (
                                <span style={{ color: "#52c41a", fontSize: "11px", fontWeight: 700, marginLeft: "4px", display: "inline-flex", alignItems: "center", gap: "3px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    <span style={{ height: "6px", width: "6px", borderRadius: "50%", background: "#52c41a", display: "inline-block" }}></span>
                                    LIVE
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: "12px", background: "#f8f9fa", padding: "10px 14px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                        <div style={{ flex: 1, minWidth: "300px" }}>
                            <AIGenerateAction 
                                onGenerateSuccess={handleAIGenerateSuccess}
                                category="DecisionBox"
                                mode="json"
                                showText={true}
                                contextInstructions="Generate content for the course decision box with 4 specific sections. Output STRICTLY as a JSON object with keys: 'recommended_for', 'not_recommended_for', 'price_guidance', 'final_verdict'."
                                systemPromptDefault="You are a helpful assistant for discovering courses. Generate high-quality SEO-optimized decision box content. Output as JSON."
                                buttonText="Generate All Sections"
                                courseId={effectiveId}
                            />
                        </div>
                    </div>
                </div>

                <Tabs defaultActiveKey="recommended_for" onChange={onTabChange} type="card" className="decision-box-tabs">
                    <TabPane tab="Recommended For" key="recommended_for" />
                    <TabPane tab="Not Recommended For" key="not_recommended_for" />
                    <TabPane tab="Price Guidance" key="price_guidance" />
                    <TabPane tab="Final Verdict" key="final_verdict" />
                </Tabs>

                <div className="mt-3">
                    <TextEditor
                        key={activeTab}
                        description={formData[activeTab]}
                        onChange={handleEditorChange}
                        height="300px"
                    />
                </div>

                {/* Footer Action */}
                <div className="d-flex justify-content-end mt-3 pt-2" style={{ borderTop: "1px solid #f0f0f0" }}>
                    <Button 
                        type="primary" 
                        onClick={handleSave} 
                        loading={loading} 
                        style={{ 
                            minWidth: "140px",
                            height: "36px", 
                            fontSize: "14px", 
                            fontWeight: 500, 
                            borderRadius: "6px", 
                            boxShadow: "0 2px 4px rgba(24, 144, 255, 0.15)",
                            background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                            border: "none"
                        }}
                    >
                        Save Changes
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default CourseDecisionBox;
